import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { EnvironmentOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  TripMapView,
  type LatLng,
  type MapMarker,
  type MapSettings,
} from '@ubus/shared';

const { Text, Paragraph } = Typography;

/** Sydney CBD → Bondi Beach sample route (Australian WGS-84 coordinates). */
const PREVIEW_PICKUP: LatLng = { lat: -33.8688, lng: 151.2093 };
const PREVIEW_DROPOFF: LatLng = { lat: -33.8915, lng: 151.2767 };

export interface MapSettingsForm {
  map_provider: 'google';
  google_maps_api_key: string;
}

interface MapSettingsTabProps {
  value: MapSettingsForm;
  onChange: (next: MapSettingsForm) => void;
  onSave: () => Promise<void>;
  saving: boolean;
  lastSavedAt?: string | null;
}

function toMapSettings(form: MapSettingsForm): MapSettings {
  return {
    provider: 'google',
    googleApiKey: form.google_maps_api_key.trim(),
  };
}

function MapSettingsTab({ value, onChange, onSave, saving, lastSavedAt }: MapSettingsTabProps) {
  const { t, i18n } = useTranslation();
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewNonce, setPreviewNonce] = useState(0);

  const previewSettings = useMemo(() => toMapSettings(value), [value]);
  const previewKey = `${previewNonce}-${previewSettings.googleApiKey.slice(0, 6)}`;

  const markers: MapMarker[] = useMemo(
    () => [
      { id: 'pickup', position: PREVIEW_PICKUP, title: t('mapSettings.markerPickup'), role: 'pickup' },
      { id: 'dropoff', position: PREVIEW_DROPOFF, title: t('mapSettings.markerDropoff'), role: 'dropoff' },
    ],
    [t, i18n.language]
  );

  const activeKey = value.google_maps_api_key.trim();

  const handleSave = async () => {
    if (!activeKey) {
      message.warning(t('mapSettings.requiredGoogle'));
      return;
    }
    await onSave();
    setPreviewNonce((n) => n + 1);
  };

  return (
    <Card
      title={
        <span>
          <EnvironmentOutlined style={{ marginRight: 8 }} />
          {t('mapSettings.title')}
        </span>
      }
      extra={
        lastSavedAt ? (
          <Text type="secondary">{t('mapSettings.lastSaved', { time: new Date(lastSavedAt).toLocaleString() })}</Text>
        ) : null
      }
      style={{ borderRadius: 12 }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Text strong>{t('mapSettings.googleKey')}</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {t('mapSettings.googleHelp')}
          </Paragraph>
          <Input
            value={value.google_maps_api_key}
            onChange={(e) => onChange({ ...value, google_maps_api_key: e.target.value, map_provider: 'google' })}
            placeholder="AIza..."
          />
        </div>

        <Space wrap>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            {t('mapSettings.save')}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setPreviewError(null);
              setPreviewNonce((n) => n + 1);
            }}
          >
            {t('mapSettings.reload')}
          </Button>
        </Space>

        <Card
          type="inner"
          title={t('mapSettings.previewTitle')}
          extra={
            previewError ? (
              <Tag color="error">{t('mapSettings.previewFailed')}</Tag>
            ) : activeKey ? (
              <Tag color="success">{t('mapSettings.usingDraft')}</Tag>
            ) : (
              <Tag>{t('mapSettings.enterKey')}</Tag>
            )
          }
        >
          <div style={{ height: 360, borderRadius: 8, overflow: 'hidden', border: '1px solid #f0f0f0' }}>
            {activeKey ? (
              <TripMapView
                key={previewKey}
                center={PREVIEW_PICKUP}
                zoom={11}
                markers={markers}
                route={{ from: PREVIEW_PICKUP, to: PREVIEW_DROPOFF }}
                mapSettings={previewSettings}
                onError={(msg) => setPreviewError(msg)}
                onReady={() => setPreviewError(null)}
              />
            ) : (
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fafafa',
                  color: '#999',
                }}
              >
                {t('mapSettings.previewEmpty')}
              </div>
            )}
          </div>
          {previewError && (
            <Alert type="error" message={previewError} style={{ marginTop: 12 }} showIcon />
          )}
        </Card>
      </Space>
    </Card>
  );
}

export default MapSettingsTab;
